import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Download, Phone, Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { SmsComposer } from "@/components/SmsComposer";

export async function downloadCsv(path, params, fallbackName) {
  const { data, headers } = await api.get(path, { params, responseType: "blob" });
  const m = /filename="?([^"]+)"?/.exec(headers["content-disposition"] || "");
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = m ? m[1] : fallbackName; a.click();
  URL.revokeObjectURL(url);
}

export function Contacts() {
  const [items, setItems] = useState([]);
  const [onlyPhone, setOnlyPhone] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});

  useEffect(() => {
    api.get("/admin/contacts", { params: { only_phone: onlyPhone } }).then(({ data }) => setItems(data));
  }, [onlyPhone]);

  const shown = items.filter((c) => !q || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));
  const withPhone = items.filter((c) => c.phone).length;
  const selectable = shown.filter((c) => c.phone);
  const selected = items.filter((c) => c.phone && sel[c.phone]);
  const allShownSelected = selectable.length > 0 && selectable.every((c) => sel[c.phone]);
  const toggleAll = () => {
    const n = { ...sel };
    selectable.forEach((c) => { if (allShownSelected) delete n[c.phone]; else n[c.phone] = true; });
    setSel(n);
  };

  return (
    <div className="rounded-2xl bg-white border border-border p-5" data-testid="contacts-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-olive">Base de contacts</h2>
          <p className="text-sm text-muted-foreground">{items.length} client(s) · <span className="text-olive font-semibold" data-testid="contacts-phone-count">{withPhone} avec téléphone</span> — exportez pour vos campagnes SMS.</p>
        </div>
        <Button onClick={() => downloadCsv("/export/contacts.csv", { only_phone: onlyPhone }, "contacts.csv")} className="bg-olive text-white" data-testid="contacts-export-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <div className="flex items-center gap-4 mt-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom, email, numéro…" className="h-9" data-testid="contacts-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={onlyPhone} onCheckedChange={setOnlyPhone} data-testid="contacts-only-phone" /> Avec téléphone uniquement
        </label>
        <label className="flex items-center gap-2 text-sm text-olive font-medium">
          <Checkbox checked={allShownSelected} onCheckedChange={toggleAll} data-testid="contacts-select-all" /> Tout sélectionner ({selectable.length})
        </label>
        {selected.length > 0 && <button onClick={() => setSel({})} className="text-xs text-muted-foreground underline" data-testid="contacts-clear-selection">Vider la sélection ({selected.length})</button>}
      </div>
      <div className="mt-4 max-h-96 overflow-y-auto divide-y divide-border" data-testid="contacts-list">
        {shown.slice(0, 300).map((c, i) => (
          <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
            <div className="flex items-center gap-3 min-w-0">
              <Checkbox checked={!!sel[c.phone]} disabled={!c.phone} onCheckedChange={(v) => setSel((s) => { const n = { ...s }; if (v) n[c.phone] = true; else delete n[c.phone]; return n; })} data-testid={`contact-check-${i}`} />
              <div className="min-w-0">
                <p className="font-semibold text-olive truncate">{c.name || "—"} <span className="text-xs font-normal text-muted-foreground">· {c.source} · {c.stays} séjour(s)</span></p>
                <p className="text-muted-foreground truncate">{c.email || "email inconnu"}</p>
              </div>
            </div>
            <span className={`flex items-center gap-1 shrink-0 ${c.phone ? "text-olive font-semibold" : "text-muted-foreground"}`}>
              <Phone className="w-3.5 h-3.5" /> {c.phone || "—"}
            </span>
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-muted-foreground py-4">Aucun contact.</p>}
      </div>
      <SmsComposer selected={selected} onSent={() => setSel({})} />
    </div>
  );
}
